// =====================================================
// 📁 POSProductListView.tsx
// =====================================================

import { useMemo } from 'react';

import { AnimatePresence } from 'framer-motion';

import { ProductPanelItem } from '../../../../../types/productpanel/ProductPanelItem';

import useProductPanel from '../../../../../hooks/productpanel/useProductPanel';

import POSProductPlaceholder from '../POSProductPlaceholder';

import UniversalTablePagination from '../../../../Tables/universalRecord/UniversalTablePagination';

import POSProductItem from '../product-item/POSProductItem';

// import POSProductTableHeader from './POSProductTableHeader';
import ProductTableSkeleton from '../../../../Shared/ProductTableSkeleton';
import ProductTableHeader from '../../../../Shared/ProductTableHeader';

interface Props {
  onAddProductClick: (product: ProductPanelItem) => void;
  mode?: 'list' | 'card';
}

const POSProductListView: React.FC<Props> = ({ onAddProductClick, mode }) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const {
    filteredProducts,

    paginator,

    pageSize,

    fetchFilteredProducts,

    currentFilter,

    updatePageSize,

    updateCurrentPage,

    isLoading,
  } = useProductPanel();

  // =====================================================
  // 🔹 Pagination
  // =====================================================

  const handlePageChange = (page: number) => {
    updateCurrentPage(page);

    fetchFilteredProducts(currentFilter || {}, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    updatePageSize(size);

    updateCurrentPage(0);

    fetchFilteredProducts(currentFilter || {}, 0, size);
  };

  // =====================================================
  // 🔹 Unique Products
  // =====================================================

  const uniqueProducts = useMemo(() => {
    return filteredProducts.filter(
      (p, index, self) =>
        index === self.findIndex((q) => q.id === p.id && q.name === p.name)
    );
  }, [filteredProducts]);

  // =====================================================
  // 🔹 Loading
  // =====================================================

  if (isLoading) {
    return <ProductTableSkeleton showAction />;
  }

  // =====================================================
  // 🔹 Empty
  // =====================================================

  if (uniqueProducts.length === 0) {
    return <POSProductPlaceholder mode={mode} />;
  }

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div className="space-y-4">
      {/* ===================================================== */}
      {/* Table */}
      {/* ===================================================== */}

      <div
        className="
          overflow-hidden

          rounded-xl
          border

          border-stroke
          bg-white

          shadow-1

          dark:border-strokedark

          dark:bg-boxdark
          lg:rounded-2xl
        "
      >
        {/* ===================================================== */}
        {/* Header */}
        {/* ===================================================== */}

        <div className="hidden sm:block">
          <ProductTableHeader showAction />
        </div>

        {/* ===================================================== */}
        {/* Body */}
        {/* ===================================================== */}

        <div
          className="
            divide-y
            divide-stroke

            dark:divide-strokedark
          "
        >
          <AnimatePresence mode="popLayout">
            {uniqueProducts.map((product, index) => (
              <POSProductItem
                key={`${product.id}-${index}`}
                product={product}
                mode="list"
                onAddToCart={onAddProductClick}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* ===================================================== */}
      {/* Pagination */}
      {/* ===================================================== */}

      <div
        className="
          mt-4

          w-full
          overflow-x-auto
        "
      >
        <UniversalTablePagination
          paginator={paginator}
          data={uniqueProducts}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default POSProductListView;
